import { Box, HStack } from '@televet/kibble-ui/build/chakra';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import React, { useMemo, useState } from 'react';
import { FeatureFlagName, GraphQLFetchPolicies } from 'shared/enums';
import { WildcardValues } from 'shared/enums/WildcardOptions';
import useClinicUser from 'shared/hooks/useClinicUser';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import {
  CareCreditPreapprovalDecision,
  ClinicPetParent,
  useGetCareCreditPreapprovalChecksQuery,
  useGetCareCreditMessageTemplateLazyQuery,
  useGetOrCreateActiveClientChannelMutation,
  useCreateChannelMessageMutation,
  MessageType,
} from 'shared/types/graphql';

interface CareCreditProfileCheckProps {
  clinicPetParent: Pick<ClinicPetParent, 'id' | 'firstName' | 'lastName'>;
}

const CareCreditProfileCheck = ({ clinicPetParent }: CareCreditProfileCheckProps): JSX.Element | null => {
  const { currentClinicId, clinicUserId } = useClinicUser();
  const { isFeatureEnabled } = useFeatureFlag();
  const toast = useToast();

  const hasCareCredit = isFeatureEnabled(FeatureFlagName.CareCredit);

  const { data } = useGetCareCreditPreapprovalChecksQuery({
    variables: { clinicPetParentIds: [clinicPetParent.id] },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    skip: !hasCareCredit,
  });

  const careCreditPreapprovalCheck = data?.findManyCareCreditPreapprovalCheck?.[0];
  const preapprovalUrl = careCreditPreapprovalCheck?.preapprovalUrl ?? 'https://carecredit.com';

  const [getMessageTemplate] = useGetCareCreditMessageTemplateLazyQuery();
  const [getOrCreateActiveChannel] = useGetOrCreateActiveClientChannelMutation();
  const [createChannelMessage] = useCreateChannelMessageMutation();
  const [isLoading, setIsLoading] = useState(false);

  const handleSendLinkClick = async (): Promise<void> => {
    if (!currentClinicId) {
      return;
    }
    setIsLoading(true);
    const [careCreditTemplate, channel] = await Promise.all([
      (
        await getMessageTemplate({ variables: { clinicId: currentClinicId ?? '' } })
      ).data?.findFirstMessageTemplate?.body,
      (
        await getOrCreateActiveChannel({ variables: { data: { clinicPetParentIds: [clinicPetParent.id] } } })
      ).data?.getOrCreateActiveClientChannel,
    ]);

    const currentUserChannelMember = channel?.channelMembers.find(({ userId }) => userId === clinicUserId);

    if (!channel || !currentUserChannelMember) {
      if (!channel) {
        console.error('Unable to find or create a channel', { clinicPetParentId: clinicPetParent.id });
      } else if (!currentUserChannelMember) {
        console.error('No channel member exists for current user', { clinicPetParentId: clinicPetParent.id });
      }
      toast({
        status: 'error',
        title: 'Error! There was a problem sending the CareCredit link. Please try again.',
      });
      setIsLoading(false);
      return;
    }

    const draftMessageText = careCreditTemplate
      ? careCreditTemplate.replaceAll(WildcardValues.CareCreditPreapprovalUrl, preapprovalUrl)
      : `Here's the info you requested. You've been preapproved for a CareCredit credit card. Visit ${preapprovalUrl} to view offer & apply.`;
    await createChannelMessage({
      variables: {
        data: {
          channel: { connect: { id: channel?.id } },
          messageType: MessageType.Message,
          index: 0,
          body: draftMessageText,
          author: { connect: { id: currentUserChannelMember.id } },
          forceReadReceipt: true,
        },
      },
    });
    toast({
      status: 'success',
      title: 'Success! CareCredit link sent.',
    });
    setIsLoading(false);
  };

  const { isApproved, isExpired, hasApplied } = useMemo(() => {
    const expirationDate = careCreditPreapprovalCheck?.offerExpiresAt
      ? new Date(careCreditPreapprovalCheck.offerExpiresAt)
      : null;
    return {
      isApproved: careCreditPreapprovalCheck?.decision === CareCreditPreapprovalDecision.Approved,
      isExpired: expirationDate && expirationDate < new Date(),
      hasApplied: !!careCreditPreapprovalCheck?.applicationStatus,
    };
  }, [careCreditPreapprovalCheck]);

  if (isApproved && !isExpired && !hasApplied) {
    return (
      <Alert
        flexDirection="column"
        marginX={3}
        marginBottom={3}
        title={`${clinicPetParent.firstName} is preapproved for the CareCredit credit card.`}
        hideCloseButton
        hideIcon
        status="warning"
        description={
          <>
            <Box>
              <Text variant="subtle">
                {`Send ${clinicPetParent.firstName} a link to view and accept preapproval offer.`}
              </Text>
            </Box>
            <HStack>
              <Button variant="primary" size="xs" isLoading={isLoading} onClick={handleSendLinkClick}>
                Send Link
              </Button>
            </HStack>
          </>
        }
      />
    );
  }

  return null;
};

export default CareCreditProfileCheck;
